import { type FC } from "react";

import { IntakeInterviewStatusEnum } from "@app_schema";

import { pluralize } from "@application/utilities/pluralize";

import { BasicLink } from "@styled/basic_link";
import { Button } from "@styled/button";
import { DateTimeFormatter } from "@styled/date_time_formatter";
import { ErrorLine } from "@styled/error_line";
import { NoticeLine } from "@styled/notice_line";

import { IntakePane } from "../intake_pane";
import { DeclinedNewLink, IntakeLinkBlock } from "./intake_link_block";
import { type IntakeProject, type Interview } from "./types";
import { useIntakeInterviewActions } from "./use_intake_interview_actions";

const REVIEW_ERROR = "The intake couldn't be marked reviewed. Try again.";

const answersSaved = (count: number) =>
  `${count} ${pluralize(count, "answer", "answers")} saved`;

// One card for where the interview stands, with the office's one next step.
export const IntakeStatusCard: FC<{
  project: IntakeProject;
  flat: boolean;
}> = ({ project, flat }) => {
  const interview = project.intakeInterview;

  return interview ? (
    <InterviewCard project={project} interview={interview} flat={flat} />
  ) : (
    <NotStartedCard project={project} flat={flat} />
  );
};

const NotStartedCard: FC<{ project: IntakeProject; flat: boolean }> = ({
  project,
  flat,
}) => {
  const { makeLink, linking } = useIntakeInterviewActions(project.id);

  return (
    <IntakePane
      flat={flat}
      title="Not started"
      subtitle="The claimant answers the intake questions out loud, from a link on their phone or computer. It takes about 30–40 minutes. The doctor reviews the outline before the exam."
    >
      {project.claimantIdentityMissing && (
        <NoticeLine>
          Add the claimant’s {project.claimantIdentityMissing} to create a link.{" "}
          <BasicLink
            to={`/dashboard/projects/${project.id}/edit?section=intake`}
          >
            Edit case
          </BasicLink>
        </NoticeLine>
      )}
      {project.policy.modify && (
        <Button
          color="navy"
          loading={linking}
          disabled={!!project.claimantIdentityMissing}
          onClick={() => void makeLink()}
        >
          Create claimant link
        </Button>
      )}
    </IntakePane>
  );
};

const InterviewCard: FC<{
  project: IntakeProject;
  interview: Interview;
  flat: boolean;
}> = ({ project, interview, flat }) => {
  const { markReviewed, reviewing, reviewFailed } = useIntakeInterviewActions(
    project.id,
  );
  const link = <IntakeLinkBlock project={project} interview={interview} />;

  switch (interview.status) {
    case IntakeInterviewStatusEnum.LinkReady:
      return (
        <IntakePane
          flat={flat}
          title="Claimant link ready"
          subtitle="Send it to the claimant by email or text. They check their date of birth and full name before it starts."
        >
          {link}
        </IntakePane>
      );
    case IntakeInterviewStatusEnum.Started:
      return (
        <IntakePane
          flat={flat}
          title="Started"
          subtitle={`${interview.currentPart ? `At Part ${interview.currentPart.number} · ${interview.currentPart.name}. ` : ""}${answersSaved(interview.answersCount)}. The claimant can finish with the same link.`}
        >
          {link}
        </IntakePane>
      );
    case IntakeInterviewStatusEnum.Declined:
      return (
        <IntakePane
          flat={flat}
          title="Declined"
          subtitle={
            <>
              The claimant declined the AI interview (
              <DateTimeFormatter iso={interview.declinedAt} format="date" />
              ). Take the history at the exam.
            </>
          }
        >
          {project.policy.modify && <DeclinedNewLink projectId={project.id} />}
        </IntakePane>
      );
    case IntakeInterviewStatusEnum.Finished:
      return (
        <IntakePane
          flat={flat}
          title="Finished"
          subtitle={
            <>
              <DateTimeFormatter iso={interview.finishedAt} format="date" />
              {" · "}
              {answersSaved(interview.answersCount)}.
            </>
          }
        >
          {interview.reviewedAt ? (
            <NoticeLine>
              Reviewed by {interview.reviewedBy?.name} ·{" "}
              <DateTimeFormatter iso={interview.reviewedAt} format="date" />
            </NoticeLine>
          ) : (
            project.policy.modify && (
              <>
                <Button
                  color="navy"
                  loading={reviewing}
                  onClick={() => void markReviewed()}
                >
                  Mark as reviewed
                </Button>
                {reviewFailed && <ErrorLine>{REVIEW_ERROR}</ErrorLine>}
              </>
            )
          )}
        </IntakePane>
      );
  }
};
